import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);
const TOKEN_KEY = 'muzi_token';
const USER_KEY = 'muzi_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  const persist = useCallback((token, u) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (u) {
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
    }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    try {
      const u = await authApi.me();
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login({ email, password });
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const register = useCallback(async (email, username, password, invitationCode) => {
    const data = await authApi.register({ email, username, password, invitation_code: invitationCode });
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const resetPassword = useCallback(async (adminCode, email, newPassword) => {
    const data = await authApi.resetPassword({ admin_code: adminCode, email, new_password: newPassword });
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((u) => {
      if (!u) return u;
      const merged = { ...u, ...patch };
      localStorage.setItem(USER_KEY, JSON.stringify(merged));
      return merged;
    });
  }, []);

  const requireAuth = useCallback((mode = 'login') => {
    if (user) return true;
    setAuthMode(mode);
    setAuthOpen(true);
    return false;
  }, [user]);

  // Stable actions — every callback below has stable deps so this memo
  // only changes when one of them legitimately needs to change.
  const actions = useMemo(() => ({
    login, register, logout, refresh, updateUser, resetPassword,
    setAuthOpen, setAuthMode,
  }), [login, register, logout, refresh, updateUser, resetPassword]);

  const value = useMemo(() => ({
    ...actions,
    user, loading, authOpen, authMode, requireAuth,
  }), [actions, user, loading, authOpen, authMode, requireAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
